"use client";
import React, { useCallback, useMemo, useState } from "react";
import CustomCalcField from "@/components/ui/CustomCalcField";
import { allLumpsumCalculation, calculateStepUpSIP, allSwpCalculation } from "@/utils/allCalculators";
import HomeCalcOutputPart from "../others/HomeCalcOutputPart";
import HomeSectionHeading from "../others/HomeSectionHeading";
import { ROOT_URL } from "@/utils/gloableVariables";
import Icon from "../icons/Icons";
import Link from "next/link";

type CalculatorType = "sip" | "lumpsum" | "swp";
const CALCULATOR_TYPE_LIST: CalculatorType[] = ["sip", "lumpsum", "swp"];

export default function HomeCalculatorSection() {
  const [calcType, setCalcType] = useState<CalculatorType>("sip");

  const [amount, setAmount] = useState<number>(10000);
  const [duration, setDuration] = useState<number>(5);
  const [returns, setReturns] = useState<number>(12);

  const [stepUpVal, setStepUpVal] = useState<number>(0);
  const [incrementType, setIncrementType] = useState<"₹" | "%">("₹");
  const [withdrawalAmount, setWithdrawalAmount] = useState<number>(100);

  const inputLimits = useMemo(() => {
    const MIN_AMOUNT = calcType === "sip" ? 100 : calcType === "swp" ? 5000 : 1000;
    const MAX_AMOUNT = calcType === "sip" ? 1000000 : calcType === "swp" ? 300000000 : 200000000;

    return {
      MIN_AMOUNT,
      MAX_AMOUNT,
    };
  }, [calcType]);

  const calculationData = useMemo(() => {
    if (calcType == "sip") {
      const calculation = calculateStepUpSIP(amount, stepUpVal, duration, returns, "Monthly", incrementType);

      return {
        investment: Number(calculation.totalInvestment) ?? 0,
        absProfitValue: Number(calculation.totalReturns) ?? 0,
        futureValue: Number(calculation.currentBalance) ?? 0,
        yearlyReport: calculation.stepupReport.map((data) => ({
          year: Number(data.tenure),
          currentValue: Number(data.currentBalance),
        })),
      };
    }

    if (calcType == "swp") {
      const calculation = allSwpCalculation(amount, withdrawalAmount, duration, returns, "Monthly");

      return {
        investment: calculation.investmentAmount,
        absProfitValue: calculation.finalValue,
        futureValue: calculation.totalWithdrawn,
        yearlyReport: calculation.reportData.map((data) => ({
          year: Number(data.tenure),
          currentValue: Number(data.currValue),
        })),
      };
    }

    return allLumpsumCalculation(amount, duration, returns, 0, "futureValueCalc");
  }, [amount, duration, returns, stepUpVal, withdrawalAmount, calcType, incrementType]);

  return (
    <>
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
        <HomeSectionHeading
          heading={`Project Your <i class="text-primaryBlue font-black">Wealth</i>`}
          textAlign="text-left"
          spacing=""
        />

        <div className="w-full max-w-md flex items-center justify-between gap-px border border-[#D4D4D4] rounded-full p-0.5">
          {CALCULATOR_TYPE_LIST.map((type) => (
            <button
              key={type}
              onClick={() => setCalcType(type)}
              className={[
                "w-full xs:max-w-36 sm:shrink-0 py-2 rounded-full md:text-xl md:leading-7 leading-1.2 transition",
                calcType === type
                  ? "bg-[#734ACA] font-bold italic text-white"
                  : "text-primaryBlack sm:hover:bg-[#724aca33]",
                type === "lumpsum" ? "capitalize" : "uppercase",
              ].join(" ")}
            >
              {type}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-col md:flex-row items-stretch gap-5 2xl:gap-10 mb-8">
        {/* LEFT */}
        <div className="w-full md:w-[47%] flex flex-col justify-between gap-3 md:gap-0 rounded-2xl sm:rounded-4xl 2md:rounded-8xl border border-[#EDEDED] p-4 xs:p-6 2md:p-8">
          <CustomCalcField
            label={calcType === "sip" ? "Monthly Inv. Amount" : "Investment Amount"}
            amountValue={amount}
            min={inputLimits.MIN_AMOUNT}
            max={inputLimits.MAX_AMOUNT}
            step={500}
            unit="₹"
            addedValuesList={[50000, 80000, 100000]}
            onChange={setAmount}
            className="space-y-2 lg:space-y-1"
          />

          <CustomCalcField
            label="Duration"
            amountValue={duration}
            min={1}
            max={50}
            unit="Years"
            unitPosition="right"
            onChange={setDuration}
            className="space-y-1"
          />

          <CustomCalcField
            label="Expected Returns (p.a.)"
            amountValue={returns}
            min={1}
            max={30}
            unit="%"
            unitPosition="right"
            allowDecimal
            onChange={setReturns}
            className="space-y-1"
          />

          {calcType === "sip" && (
            <CustomCalcField
              label="Annual Step-Up"
              amountValue={stepUpVal}
              // amountValue={incrementType === "₹" ? 1000 : 5}
              unitPosition={incrementType == "₹" ? "left" : "right"}
              min={incrementType == "₹" ? 100 : 5}
              max={incrementType == "₹" ? 100000 : 50}
              unit={incrementType}
              allowDecimal={incrementType == "%"}
              showAmountModeToggle
              amountMode={incrementType}
              onAmountModeChange={(e) => {
                setStepUpVal(5);
                setIncrementType(e);
              }}
              onChange={setStepUpVal}
              useRangeSlider={false}
            />
          )}

          {calcType === "swp" && (
            <CustomCalcField
              label="Withdraw per Month"
              amountValue={withdrawalAmount}
              min={100}
              max={1000000}
              unit="₹"
              unitPosition="left"
              onChange={setWithdrawalAmount}
              useRangeSlider={false}
            />
          )}
        </div>

        {/* RIGHT */}
        <HomeCalcOutputPart calculation={calculationData} calcType={calcType} />
      </div>

      <div className="flex flex-col xs:flex-row items-center justify-center gap-3 sm:gap-5 text-center">
        <a
          href={ROOT_URL + "start-sip.php"}
          className="w-full max-w-80 flex items-center justify-center gap-2 border sm:border-2 border-primaryBlue rounded-xl sm:rounded-2xl bg-primaryBlue text-sm sm:text-base sm:leading-6 lg:text-xl lg:leading-7 leading-1.5 font-bold text-white py-3 sm:py-4 shine-button-hover"
        >
          Start SIP with this plan
          <Icon.rightArrow className="shrink-0 w-5 h-5 sm:w-6 sm:h-6" />
        </a>
        <Link
          href="/tools-and-calculators"
          className="w-full max-w-80 border sm:border-2 border-primaryBlue rounded-xl sm:rounded-2xl text-sm sm:text-base sm:leading-6 lg:text-xl lg:leading-7 leading-1.5 font-medium text-primaryBlue py-3 sm:py-4 transition hover:bg-primaryBlue/90 hover:text-white"
        >
          See more calculators
        </Link>
      </div>
    </>
  );
}
